import React, { createContext, useContext, useReducer, useEffect, ReactNode, useMemo, useCallback, useState, startTransition, useRef } from 'react';
import { GameState, GuessResult, StockData, CategoryGameContextType, CategoryGameStorage, GameStatistics } from '../types/game';
import { StockCategoryFilter } from '../types/stockCategories';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useCategoryTimer } from '../hooks/useCategoryTimer';
import { useOverallStreak } from '../hooks/useOverallStreak';
import { createLogger } from '../utils/logger';
import { rescheduleAfterPlay } from '../services/notifications';
import { trackGameCompletionForReview } from '../services/appReview';
import { normalizeStockOption } from '../utils/stockOptionFormatting';
import { sanitizeGuess, extractGuessSymbol } from '../utils/guessMatching';
import { submitGameResult, devResetGameCategory } from '../utils/api';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';
import { buildShareLine } from '../components/game/ShareEmoji';
import { supabase } from '../config/supabase';
import { getOrCreateSessionId } from '../utils/sessionId';
import { getOrCreateDeviceId } from '../utils/deviceId';
import { useAuth } from '../hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';

interface GameContextType {
  gameState: GameState;
  dispatch: React.Dispatch<GameAction>;
  resetGame: () => void;
  saveGameState: () => void;
  loadGameState: () => void;
}

type GameAction =
  | { type: 'SET_STOCK'; payload: StockData }
  | { type: 'ADD_GUESS'; payload: GuessResult }
  | { type: 'SET_ATTEMPTS'; payload: number }
  | { type: 'SET_GAME_STATUS'; payload: 'playing' | 'won' | 'lost' }
  | { type: 'SET_VISIBLE_HINTS'; payload: number }
  | { type: 'SET_STREAK'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CURRENT_GUESS'; payload: string }
  | { type: 'RESET_GAME' }
  | { type: 'LOAD_STATE'; payload: Partial<GameState> }
  | { type: 'SWITCH_CATEGORY'; payload: StockCategoryFilter };

const initialGameState: GameState = {
  currentGuess: '',
  guesses: [],
  gameStatus: 'playing',
  currentRow: 0,
  solution: null,
  attempts: 4,
  visibleHints: 1,
  streak: 0,
  isLoading: false,
  error: null,
  gameDate: getCurrentGameTimestamp(),
  lastPlayedDate: null,
  isGameComplete: false,
  version: 1
};

const MAX_HINTS = 4;

const gameContextLogger = createLogger('GameContext', { flag: 'GAME_STATE' });

const logGameContext = (
  level: 'debug' | 'info' | 'warn',
  message: string,
  details?: Record<string, unknown>
) => {
  if (!gameContextLogger.isEnabled()) {
    return;
  }

  const payload = details ? [message, details] : [message];
  switch (level) {
    case 'info':
      gameContextLogger.info(...payload);
      break;
    case 'warn':
      gameContextLogger.warn(...payload);
      break;
    default:
      gameContextLogger.debug(...payload);
  }
};

/**
 * Extract ticker symbol from a guess for backend submission
 * Handles both formatted display strings and special values like "SKIPPED"
 */
const extractTickerForSubmission = (companyValue: string): string => {
  // Special case: SKIPPED remains as-is
  if (companyValue === 'SKIPPED') {
    return 'SKIPPED';
  }

  // Empty padding from give-up should remain empty
  if (!companyValue || companyValue.trim() === '') {
    return '';
  }

  // Use normalizeStockOption to parse formatted strings
  const normalized = normalizeStockOption(companyValue);

  // If we successfully extracted a ticker, use it
  if (normalized.ticker) {
    return normalized.ticker;
  }

  // Fallback: try extractGuessSymbol for edge cases
  const extracted = extractGuessSymbol(companyValue);
  if (extracted) {
    return extracted;
  }

  // Last resort: return original value (shouldn't happen in normal flow)
  console.warn('Failed to extract ticker from guess:', companyValue);
  return companyValue;
};

const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'SET_STOCK':
      return { ...state, solution: action.payload };
    case 'ADD_GUESS':
      return {
        ...state,
        guesses: [...state.guesses, action.payload],
        currentRow: state.currentRow + 1,
        currentGuess: '' // Clear current guess after adding
      };
    case 'SET_ATTEMPTS':
      return { ...state, attempts: action.payload };
    case 'SET_GAME_STATUS':
      return {
        ...state,
        gameStatus: action.payload,
        isGameComplete: action.payload !== 'playing'
      };
    case 'SET_VISIBLE_HINTS':
      return { ...state, visibleHints: action.payload };
    case 'SET_STREAK':
      return { ...state, streak: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CURRENT_GUESS':
      return { ...state, currentGuess: action.payload };
    case 'RESET_GAME':
      return {
        ...initialGameState,
        solution: state.solution, // Keep the current stock
        gameDate: getCurrentGameTimestamp()
      };
    case 'LOAD_STATE':
      return { ...state, ...action.payload };
    case 'SWITCH_CATEGORY':
      // Category switching is handled at the provider level
      return state;
    default:
      return state;
  }
};

const GameContext = createContext<GameContextType | undefined>(undefined);

// Split contexts for better performance
interface CategoryGameActions {
  switchCategory: (category: StockCategoryFilter) => void;
  getCategoryGameState: (category: StockCategoryFilter) => GameState;
  resetCategoryGame: (category: StockCategoryFilter) => void;
  submitGuess: (guess: string) => Promise<void>;
  handleGiveUp: () => void;
  resetGame: () => void;
  loadGameState: () => void;
  saveGameState: () => void;
  updateGameState: (updates: Partial<GameState>) => void;
  updateStockData: (newStockData: StockData) => void;
  setMarketCapLookup: (m: Record<string, number>) => void;
  initializeGame: () => Promise<void>;
  validateGuess: (guess: string, validOptions?: string[]) => { isValid: boolean; error?: string };
  getGameStatistics: () => GameStatistics;
  shareGame: () => string;
  // Dev-only feature (conditionally available in development mode)
  devResetCategory?: (category: StockCategoryFilter) => Promise<void>;
}

interface CategoryGameState {
  currentCategory: StockCategoryFilter;
  gameState: GameState;
  devMode?: boolean;
}

// Separate contexts for stable actions and changing state
const CategoryGameActionsContext = createContext<CategoryGameActions | undefined>(undefined);
const CategoryGameStateContext = createContext<CategoryGameState | undefined>(undefined);

// Legacy combined context for backward compatibility
const CategoryGameContext = createContext<CategoryGameContextType | undefined>(undefined);

interface GameProviderProps {
  children: ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [gameState, dispatch] = useReducer(gameReducer, initialGameState);
  const [savedState, setSavedState] = useLocalStorage<Partial<GameState>>('wallstreetle_gameState', {});

  const saveGameState = useCallback(() => {
    const stateToSave = {
      stockSymbol: gameState.solution?.Symbol,
      timestamp: gameState.solution?.timestamp,
      guesses: gameState.guesses,
      visibleHints: gameState.visibleHints,
      attempts: gameState.attempts,
      gameStatus: gameState.gameStatus,
      streak: gameState.streak,
      gameDate: gameState.gameDate,
      lastPlayedDate: gameState.lastPlayedDate,
      isGameComplete: gameState.isGameComplete
    };
    setSavedState(stateToSave);
  }, [gameState, setSavedState]);

  const loadGameState = useCallback(() => {
    if (savedState && Object.keys(savedState).length > 0) {
      dispatch({ type: 'LOAD_STATE', payload: savedState });
    }
  }, [savedState]);

  const resetGame = useCallback(() => {
    dispatch({ type: 'RESET_GAME' });
    setSavedState({});
  }, [setSavedState]);

  // Auto-save game state when it changes
  useEffect(() => {
    if (gameState.solution?.Symbol) {
      saveGameState();
    }
  }, [
    gameState.solution?.Symbol,
    gameState.guesses.length, // Use length instead of array reference
    gameState.visibleHints,
    gameState.attempts,
    gameState.gameStatus,
    gameState.streak,
    gameState.isGameComplete,
    saveGameState
  ]);

  const contextValue: GameContextType = useMemo(() => ({
    gameState,
    dispatch,
    resetGame,
    saveGameState,
    loadGameState
  }), [gameState, dispatch, resetGame, saveGameState, loadGameState]);

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = (): GameContextType => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

// Helper functions for category-specific storage
const getCategoryStorageKey = (category: StockCategoryFilter): string => {
  return `wallstreetle_gameState_${category}`;
};

const migrateLegacyStorage = (): void => {
  const legacyKey = 'wallstreetle_gameState';
  const popularKey = getCategoryStorageKey('popular');

  const legacyData = localStorage.getItem(legacyKey);
  if (legacyData && !localStorage.getItem(popularKey)) {
    // Migrate legacy data to Popular category
    localStorage.setItem(popularKey, legacyData);
    logGameContext('info', 'Migrated legacy game state to Popular category');
  }
};

const createInitialCategoryGameState = (category: StockCategoryFilter): GameState => ({
  ...initialGameState,
  gameDate: getCurrentGameTimestamp(),
});

// Category-aware game provider
interface CategoryGameProviderProps {
  children: ReactNode;
  initialCategory?: StockCategoryFilter;
  devMode?: boolean;  // Skip all Supabase writes when true (for testing)
}

export const CategoryGameProvider: React.FC<CategoryGameProviderProps> = ({
  children,
  initialCategory = 'popular',
  devMode = false
}) => {
  // Generate unique provider ID to track re-initializations
  const [providerId] = useState(() => Math.random().toString(36).slice(2, 11));
  const renderCountRef = useRef(0);

  // Increment render counter on each render (using ref to avoid state updates)
  renderCountRef.current += 1;

  // Current category state
  const [currentCategory, setCurrentCategory] = useState<StockCategoryFilter>(initialCategory);

  // Track the last category set by switchCategory to prevent the parent effect
  // from redundantly re-loading state that switchCategory already loaded
  const lastSwitchedCategoryRef = useRef<StockCategoryFilter | null>(null);

  // Get current user for tracking
  const { user } = useAuth();

  // Get query client for invalidating balance after game completion
  const queryClient = useQueryClient();

  if (gameContextLogger.isEnabled()) {
    gameContextLogger.throttled(
      `provider-render-${providerId}`,
      200,
      'debug',
      {
        providerId,
        renderCount: renderCountRef.current,
        initialCategory,
        currentCategory
      }
    );
  }

  // Category-specific game states storage
  const [categoryStorage, setCategoryStorage] = useLocalStorage<CategoryGameStorage>('wallstreetle_categories', {});

  // Current game state (for the active category)
  const [gameState, dispatch] = useReducer(gameReducer, createInitialCategoryGameState(currentCategory));

  const todayDate = getCurrentGameTimestamp();
  const gameDate = gameState.gameDate ?? todayDate;

  const { stopTimer, resetTimer } = useCategoryTimer({
    category: currentCategory,
    gameDate,
    enabled: gameState.gameStatus === 'playing'
  });

  // Overall streak tracking (across all categories)
  const { recordPlay: recordOverallStreak } = useOverallStreak();

  // Run migration on component mount
  useEffect(() => {
    migrateLegacyStorage();
  }, []);

  // Load game state for current category
  const loadCategoryGameState = useCallback((category: StockCategoryFilter): GameState => {
    const storage = categoryStorage[category];
    const today = getCurrentGameTimestamp();

    if (storage && storage.gameState) {
      // Validate that the saved game state is from today
      const savedGameDate = storage.gameState.gameDate;
      if (savedGameDate && savedGameDate !== today) {
        logGameContext('debug', 'Saved game state is stale', { category, savedGameDate, today });
        // Clear stale syncMeta to prevent wrong freshness decisions
        try {
          const raw = localStorage.getItem('wallstreetle_categories');
          if (raw) {
            const data = JSON.parse(raw);
            if (data[category]?.syncMeta) {
              delete data[category].syncMeta;
              localStorage.setItem('wallstreetle_categories', JSON.stringify(data));
            }
          }
        } catch { /* ignore */ }
        return createInitialCategoryGameState(category);
      }

      logGameContext('debug', 'Restoring saved game state', { category, savedGameDate });
      const normalizedGuesses = (storage.gameState.guesses || []).map((guess: GuessResult) => ({
        ...guess,
        company: normalizeStockOption(guess.company || '').display || (guess.company || '')
      }));

      return {
        ...createInitialCategoryGameState(category),
        ...storage.gameState,
        guesses: normalizedGuesses
      };
    }

    logGameContext('debug', 'No saved game state found', { category });
    return createInitialCategoryGameState(category);
  }, [categoryStorage]);

  // Check if saved game state is for a different stock (stock has rotated) or a different day
  const isStockChanged = useCallback((savedStockSymbol: string | undefined, currentStockSymbol: string | undefined, savedGameDate?: string): boolean => {
    const today = getCurrentGameTimestamp();

    // If we have a saved date and it's stale, treat as changed even without symbols
    if (savedGameDate && savedGameDate !== today) {
      logGameContext('debug', 'Game date changed, treating as new stock', {
        previousDate: savedGameDate,
        currentDate: today
      });
      return true;
    }

    // Need both symbols to compare
    if (!savedStockSymbol || !currentStockSymbol) return false;

    // Check if stock symbol has changed
    if (savedStockSymbol !== currentStockSymbol) return true;

    return false;
  }, []);

  // Strip large, refetchable fields from solution before persisting to localStorage
  const stripSolutionForStorage = (solution: StockData) => {
    const { chart_data, processed_financial_metrics, ...rest } = solution;
    return rest;
  };

  // Save game state for current category - stabilized with useCallback
  const saveCategoryGameState = useCallback((category: StockCategoryFilter, state: GameState) => {
    const stateToSave = {
      stockSymbol: state.solution?.Symbol,
      timestamp: state.solution?.timestamp,
      solution: state.solution ? stripSolutionForStorage(state.solution) : null,
      guesses: state.guesses,
      visibleHints: state.visibleHints,
      attempts: state.attempts,
      gameStatus: state.gameStatus,
      streak: state.streak,
      gameDate: state.gameDate,
      lastPlayedDate: state.lastPlayedDate,
      isGameComplete: state.isGameComplete,
      timerResult: state.timerResult
    };

    setCategoryStorage(prev => ({
      ...prev,
      [category]: {
        gameState: stateToSave,
        lastSaved: new Date().toISOString(),
        version: 1
      }
    }));
  }, [setCategoryStorage]);

  // Switch to different category
  const switchCategory = useCallback((category: StockCategoryFilter) => {
    logGameContext('info', 'Switching category', {
      from: currentCategory,
      to: category
    });

    // Don't switch if we're already on this category
    if (currentCategory === category) {
      logGameContext('debug', 'Requested category matches current category', { category });
      return;
    }

    // Use React 18's startTransition for better performance and atomic state updates
    startTransition(() => {
      // Save current state first
      saveCategoryGameState(currentCategory, gameState);

      // Mark that switchCategory is handling this category's state load,
      // so the parent useEffect on currentCategory can skip its redundant load
      lastSwitchedCategoryRef.current = category;

      // Update category atomically – rely on snapshot cache to hydrate instantly
      setCurrentCategory(category);

      // Reset game state for new category
      dispatch({ type: 'RESET_GAME' });

      // Load appropriate state for the new category
      const newGameState = loadCategoryGameState(category);
      const today = getCurrentGameTimestamp();

      if (newGameState.gameDate !== today) {
        // New day or first time playing this category
        logGameContext('debug', 'New day or first time for category, keeping reset state', { category, today });
        dispatch({
          type: 'LOAD_STATE', payload: {
            ...createInitialCategoryGameState(category),
            gameDate: today
          }
        });
      } else {
        // Same day, restore saved state (solution is now persisted to localStorage)
        logGameContext('debug', 'Loading saved state for category', { category });

        dispatch({
          type: 'LOAD_STATE', payload: {
            ...newGameState,
            isLoading: !newGameState.solution, // Only loading if no saved solution
            error: null
          }
        });
      }

      logGameContext('info', 'Category switch completed', { category });
    });
  }, [currentCategory, gameState, saveCategoryGameState, loadCategoryGameState]);

  // Get game state for any category
  const getCategoryGameState = useCallback((category: StockCategoryFilter): GameState => {
    if (category === currentCategory) {
      return gameState;
    }
    return loadCategoryGameState(category);
  }, [currentCategory, gameState, loadCategoryGameState]);

  // Reset specific category
  const resetCategoryGame = useCallback((category: StockCategoryFilter) => {
    if (category === currentCategory) {
      dispatch({ type: 'RESET_GAME' });
    }

    // Clear storage for this category
    setCategoryStorage(prev => {
      const updated = { ...prev };
      delete updated[category];
      return updated;
    });
  }, [currentCategory, setCategoryStorage]);

  // Dev-only: Reset game state for testing/recording purposes
  // Clears localStorage and Supabase game_plays record, then reloads page
  const devResetCategory = useCallback(async (category: StockCategoryFilter) => {
    if (process.env.NODE_ENV !== 'development') {
      console.error('devResetCategory is only available in development mode');
      return;
    }

    try {
      // 1. Call backend to delete Supabase game_plays record
      await devResetGameCategory(category);

      // 2. Clear localStorage for this category
      setCategoryStorage(prev => {
        const updated = { ...prev };
        delete updated[category];
        return updated;
      });

      // 3. Reload page to start fresh
      window.location.reload();
    } catch (error) {
      console.error('Failed to reset game category:', error);
      throw error;
    }
  }, [setCategoryStorage]);

  // Standard game functions adapted for categories - stabilized callbacks
  const resetGame = useCallback(() => {
    resetCategoryGame(currentCategory);
    resetTimer();
  }, [resetCategoryGame, currentCategory, resetTimer]);

  // Stabilized save function using refs to avoid dependencies
  const saveGameStateRef = useRef<() => void>(() => {});
  saveGameStateRef.current = () => {
    saveCategoryGameState(currentCategory, gameState);
  };
  
  const saveGameState = useCallback(() => {
    saveGameStateRef.current?.();
  }, []);

  // Stabilized load function using refs
  const loadGameStateRef = useRef<() => void>(() => {});
  loadGameStateRef.current = () => {
    const state = loadCategoryGameState(currentCategory);
    dispatch({ type: 'LOAD_STATE', payload: state });
  };
  
  const loadGameState = useCallback(() => {
    loadGameStateRef.current?.();
  }, []);

  // Stable update function - no dependencies needed
  const updateGameState = useCallback((updates: Partial<GameState>) => {
    dispatch({ type: 'LOAD_STATE', payload: updates });
  }, []);

  // Update stock data and reset game state if stock has changed
  // Use a ref to access current gameState without adding it as dependency
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  // Track if we've already submitted the result for this game to prevent duplicates
  const hasSubmittedResultRef = useRef<string | null>(null);

  // Market cap lookup for direction badges (populated by useCategoryStockOptions via GamePage)
  const marketCapLookupRef = useRef<Record<string, number>>({});

  const setMarketCapLookup = useCallback((m: Record<string, number>) => {
    marketCapLookupRef.current = m;
  }, []);

  const updateStockData = useCallback((newStockData: StockData) => {
    const currentGameState = gameStateRef.current;
    const currentStockSymbol = currentGameState.solution?.Symbol;
    const newStockSymbol = newStockData.Symbol;
    const savedGameDate = currentGameState.gameDate || categoryStorage[currentCategory]?.gameState?.gameDate;
    const savedStockSymbol = currentStockSymbol || categoryStorage[currentCategory]?.gameState?.stockSymbol;

    // Check for day change FIRST (before isGameComplete guard)
    // This ensures gameDate is updated even when a completed game spans the day boundary
    const today = getCurrentGameTimestamp();
    if (savedGameDate && savedGameDate !== today) {
      logGameContext('info', 'Day changed, resetting game state for new day', {
        previousDate: savedGameDate,
        currentDate: today,
        wasComplete: currentGameState.isGameComplete
      });

      // Clear stale syncMeta to prevent wrong freshness decisions
      try {
        const raw = localStorage.getItem('wallstreetle_categories');
        if (raw) {
          const data = JSON.parse(raw);
          if (data[currentCategory]?.syncMeta) {
            delete data[currentCategory].syncMeta;
            localStorage.setItem('wallstreetle_categories', JSON.stringify(data));
          }
        }
      } catch { /* ignore */ }

      resetTimer();
      hasSubmittedResultRef.current = null;

      updateGameState({
        solution: newStockData,
        visibleHints: 1,
        guesses: [],
        attempts: 4,
        gameStatus: 'playing',
        isGameComplete: false,
        currentRow: 0,
        error: null,
        gameDate: today
      });
      return;
    }

    // If game is already complete on the SAME day, don't reset it
    // This preserves completed state when user has already played today
    if (currentGameState.isGameComplete) {
      logGameContext('debug', 'Game already complete, preserving state', {
        symbol: currentStockSymbol,
        status: currentGameState.gameStatus
      });
      // Just update the solution reference but keep completed state
      updateGameState({
        solution: newStockData
      });
      return;
    }

    // Check if this is a new/different stock (by symbol, same day)
    if (isStockChanged(savedStockSymbol, newStockSymbol, savedGameDate)) {
      logGameContext('info', 'Stock changed, resetting game state', {
        previousSymbol: savedStockSymbol,
        previousDate: savedGameDate,
        newSymbol: newStockSymbol
      });

      resetTimer();

      // Reset submission tracking for new game
      hasSubmittedResultRef.current = null;

      // Reset game state for new stock but keep category
      updateGameState({
        solution: newStockData,
        visibleHints: 1, // Show first hint at start for new stock
        guesses: [], // Clear previous guesses
        attempts: 4, // Reset attempts
        gameStatus: 'playing',
        isGameComplete: false,
        currentRow: 0,
        error: null,
        gameDate: getCurrentGameTimestamp()
      });
    } else if (!currentGameState.solution) {
      // First time loading stock data OR after navigation with cleared solution
      logGameContext('debug', 'Setting initial stock data', { symbol: newStockSymbol });

      // Check if this is a completed game being restored (edge case safety net)
      if (currentGameState.isGameComplete) {
        // Just restore the solution reference, keep completed state intact
        logGameContext('debug', 'Restoring solution for completed game', { symbol: newStockSymbol });
        updateGameState({
          solution: newStockData
        });
      } else {
        // Fresh game initialization
        resetTimer();
        updateGameState({
          solution: newStockData,
          visibleHints: currentGameState.guesses.length > 0
            ? currentGameState.visibleHints // Preserve hints if restoring an in-progress game
            : 1, // Otherwise start with first hint only
          gameDate: getCurrentGameTimestamp()
        });
      }
    } else {
      // Same stock, just update the stock data
      logGameContext('debug', 'Updating existing stock data', { symbol: newStockSymbol });
      updateGameState({
        solution: newStockData
      });
    }
  }, [
    categoryStorage,
    currentCategory,
    isStockChanged,
    resetTimer,
    updateGameState
  ]);

  // Placeholder functions for full CategoryGameContextType interface
  // Use ref to access current gameState without dependency
  const submitGuessRef = useRef<(guess: string) => Promise<void>>(async () => {});
  
  // Update the ref on each render
  submitGuessRef.current = async (guess: string) => {
    const currentGameState = gameStateRef.current;

    // Prevent submissions if the game is already complete
    // This prevents replay attempts and duplicate leaderboard entries
    if (currentGameState.isGameComplete || currentGameState.gameStatus !== 'playing') {
      logGameContext('warn', 'Attempted to submit guess for completed game', {
        gameStatus: currentGameState.gameStatus,
        isGameComplete: currentGameState.isGameComplete
      });
      updateGameState({ error: 'This game is already complete. Check back tomorrow for a new puzzle!' });
      return;
    }

    const trimmedGuess = guess.trim();
    if (!trimmedGuess) {
      updateGameState({ error: 'Please enter a guess' });
      return;
    }

    if (!currentGameState.solution) {
      updateGameState({ error: 'Daily stock is still loading. Please try again in a moment.' });
      return;
    }

    const normalizedGuess = sanitizeGuess(trimmedGuess);
    const duplicateGuess = currentGameState.guesses.some(previous => sanitizeGuess(previous.company) === normalizedGuess);

    if (duplicateGuess) {
      updateGameState({ error: "You've already guessed this stock!" });
      return;
    }

    const solutionSymbol = currentGameState.solution.Symbol.toUpperCase();
    const solutionName = sanitizeGuess(currentGameState.solution.Security);
    const guessedSymbol = extractGuessSymbol(trimmedGuess);
    const isSymbolMatch = guessedSymbol ? guessedSymbol === solutionSymbol : false;
    const isNameMatch = normalizedGuess === solutionName;
    const isCorrect = Boolean(isSymbolMatch || isNameMatch);

    const normalizedDisplay = normalizeStockOption(trimmedGuess).display || trimmedGuess;

    // Compute market cap direction for guess history badges
    const guessedTicker = guessedSymbol || '';
    const guessedMcap = guessedTicker ? marketCapLookupRef.current[guessedTicker] : undefined;
    const solutionMcap = marketCapLookupRef.current[solutionSymbol];

    let marketCapDiff: { difference: number; percentage: number; direction: 'higher' | 'lower' } | undefined;
    if (!isCorrect && guessedMcap && guessedMcap > 0 && solutionMcap && solutionMcap > 0) {
      const diff = solutionMcap - guessedMcap;
      const pct = (diff / guessedMcap) * 100;
      marketCapDiff = {
        difference: Math.abs(diff),
        percentage: Math.abs(pct),
        direction: solutionMcap > guessedMcap ? 'higher' : 'lower'
      };
    }

    // Track this guess attempt (fire-and-forget, non-blocking)
    // Skip in devMode and replay mode to prevent test/replay data from being saved
    const isReplayGuess = currentGameState.gameDate && currentGameState.gameDate !== getCurrentGameTimestamp();
    if (!devMode && !isReplayGuess) {
      (async () => {
        const { error } = await supabase
          .from('game_tracking')
          .insert({
            user_id: user?.id || null,
            session_id: getOrCreateSessionId(),
            device_id: getOrCreateDeviceId(),
            category: currentCategory,
            game_date: todayDate,
            stock_symbol: solutionSymbol,
            guess_text: normalizedDisplay,
            is_correct: isCorrect,
            attempt_number: currentGameState.guesses.length + 1,
            guessed_at: new Date().toISOString(),
            ...(marketCapDiff && {
              market_cap_difference: marketCapDiff.difference,
              market_cap_percentage: marketCapDiff.percentage,
              market_cap_direction: marketCapDiff.direction
            })
          });

        // Fail silently - tracking failures should not impact gameplay
        // But always log errors for debugging (not just in development)
        if (error) {
          console.error('🔴 [GameTracking] Insert failed:', {
            category: currentCategory,
            gameDate: todayDate,
            error: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
        }
      })();
    }

    const nextGuesses: GuessResult[] = [
      ...currentGameState.guesses,
      {
        company: normalizedDisplay,
        isCorrect,
        marketCapDiff,
        guessedMarketCap: guessedMcap,
        timestamp: new Date()
      }
    ];

    const remainingAttempts = Math.max(currentGameState.attempts - 1, 0);
    const nextVisibleHints = isCorrect
      ? MAX_HINTS
      : Math.min(currentGameState.visibleHints + 1, MAX_HINTS);

    const nextState: Partial<GameState> = {
      guesses: nextGuesses,
      attempts: remainingAttempts,
      currentRow: currentGameState.currentRow + 1,
      currentGuess: '',
      visibleHints: nextVisibleHints,
      error: null,
    };

    if (isCorrect) {
      const timerResult = stopTimer();
      nextState.gameStatus = 'won';
      nextState.isGameComplete = true;
      nextState.streak = currentGameState.streak + 1;
      nextState.timerResult = timerResult;

      // Update overall streak (across all categories) — replays count too
      recordOverallStreak(currentGameState.gameDate || getCurrentGameTimestamp());
      rescheduleAfterPlay(currentGameState.streak + 1);
      trackGameCompletionForReview();
    } else if (remainingAttempts <= 0) {
      const timerResult = stopTimer();
      nextState.gameStatus = 'lost';
      nextState.isGameComplete = true;
      nextState.streak = 0;
      nextState.timerResult = timerResult;

      // Update overall streak on loss — replays count too
      recordOverallStreak(currentGameState.gameDate || getCurrentGameTimestamp());
      rescheduleAfterPlay(0);
      trackGameCompletionForReview();
    }

    updateGameState(nextState);

    // Submit game result to backend (async, non-blocking)
    // Skip in devMode to prevent test data from being saved
    // Replays are submitted normally — backend detects via gameDate and skips coins/leaderboard
    const guessGameTimestamp = currentGameState.gameDate || getCurrentGameTimestamp();
    if (!devMode && (isCorrect || remainingAttempts <= 0)) {
      const timerResult = nextState.timerResult;
      const gameTimestamp = guessGameTimestamp;
      const submissionKey = `${currentCategory}_${currentGameState.solution.Symbol}_${gameTimestamp}`;

      // Check if we've already submitted for this game (prevent duplicate leaderboard entries)
      if (hasSubmittedResultRef.current === submissionKey) {
        logGameContext('warn', 'Result already submitted for this game, skipping duplicate submission', {
          category: currentCategory,
          symbol: currentGameState.solution.Symbol
        });
        return;
      }

      const gameResult = {
        attempts: nextGuesses.length,
        isWin: isCorrect,
        solution: currentGameState.solution.Symbol,
        guesses: nextGuesses.map(g => extractTickerForSubmission(g.company)),
        completedAt: new Date().toISOString(),
        category: currentCategory,
        timeTakenMs: timerResult?.elapsedMs || 0,
        hintsUsed: currentGameState.visibleHints,
        gameDate: currentGameState.gameDate || undefined,
      };

      // Mark as submitted BEFORE making the API call to prevent race conditions
      hasSubmittedResultRef.current = submissionKey;

      // Fire and forget - don't block UI on backend submission
      submitGameResult(gameResult)
        .then(() => {
          // Force fresh balance fetch to show updated coins
          queryClient.invalidateQueries({ queryKey: ['balance'] });
        })
        .catch((error) => {
          gameContextLogger.error('Failed to submit game result to backend', { error, gameResult });
          // Don't reset the submission flag on error - better to skip than double-submit
        });
    }
  };

  const submitGuess = useCallback(async (guess: string) => {
    await submitGuessRef.current?.(guess);
  }, []);

  // Handle give up - allows player to forfeit the game
  const handleGiveUpRef = useRef<() => void>(() => {});

  handleGiveUpRef.current = () => {
    const currentGameState = gameStateRef.current;

    // Prevent give up if the game is already complete
    if (currentGameState.isGameComplete || currentGameState.gameStatus !== 'playing') {
      logGameContext('warn', 'Attempted to give up on completed game', {
        gameStatus: currentGameState.gameStatus,
        isGameComplete: currentGameState.isGameComplete
      });
      return;
    }

    if (!currentGameState.solution) {
      logGameContext('warn', 'Attempted to give up without a loaded stock');
      return;
    }

    // Pad guesses array to 4 entries
    const paddedGuesses: GuessResult[] = [...currentGameState.guesses];
    while (paddedGuesses.length < 4) {
      paddedGuesses.push({
        company: '', // Empty guess to pad to 4 attempts
        isCorrect: false,
        timestamp: new Date()
      });
    }

    // Stop timer and set to max time (30 minutes = 1,800,000 ms)
    const timerResult = {
      elapsedMs: 1800000,
      wasStarted: true
    };

    const nextState: Partial<GameState> = {
      guesses: paddedGuesses,
      attempts: 0,
      gameStatus: 'lost',
      isGameComplete: true,
      streak: 0,
      timerResult: timerResult,
      visibleHints: MAX_HINTS, // Show all hints when giving up
      error: null
    };

    // Update overall streak on give up — replays count too
    recordOverallStreak(currentGameState.gameDate || getCurrentGameTimestamp());
    rescheduleAfterPlay(0);
    trackGameCompletionForReview();

    updateGameState(nextState);

    // Submit game result to backend (async, non-blocking)
    // Skip in devMode to prevent test data from being saved
    // Replays are submitted normally — backend detects via gameDate and skips coins/leaderboard
    const giveUpGameTimestamp = currentGameState.gameDate || getCurrentGameTimestamp();
    if (!devMode) {
      const gameTimestamp = giveUpGameTimestamp;
      const submissionKey = `${currentCategory}_${currentGameState.solution.Symbol}_${gameTimestamp}`;

      // Check if we've already submitted for this game (prevent duplicate leaderboard entries)
      if (hasSubmittedResultRef.current === submissionKey) {
        logGameContext('warn', 'Result already submitted for this game, skipping duplicate submission', {
          category: currentCategory,
          symbol: currentGameState.solution.Symbol
        });
        return;
      }

      const gameResult = {
        attempts: 4, // Always 4 attempts for give up
        isWin: false,
        solution: currentGameState.solution.Symbol,
        guesses: paddedGuesses.map(g => extractTickerForSubmission(g.company)).filter(g => g !== ''), // Filter out empty padding
        completedAt: new Date().toISOString(),
        category: currentCategory,
        timeTakenMs: 1800000, // 30 minutes max time
        hintsUsed: currentGameState.visibleHints,
        gameDate: currentGameState.gameDate || undefined,
      };

      // Mark as submitted BEFORE making the API call to prevent race conditions
      hasSubmittedResultRef.current = submissionKey;

      logGameContext('info', 'Player gave up, submitting game result', {
        category: currentCategory,
        symbol: currentGameState.solution.Symbol,
        attempts: 4
      });

      // Fire and forget - don't block UI on backend submission
      submitGameResult(gameResult)
        .then(() => {
          // Force fresh balance fetch to show updated coins
          queryClient.invalidateQueries({ queryKey: ['balance'] });
        })
        .catch((error) => {
          gameContextLogger.error('Failed to submit give up result to backend', { error, gameResult });
          // Don't reset the submission flag on error - better to skip than double-submit
        });
    }
  };

  const handleGiveUp = useCallback(() => {
    handleGiveUpRef.current?.();
  }, []);

  // Handle skip - allows player to skip a guess attempt
  const handleSkipRef = useRef<() => void>(() => {});

  handleSkipRef.current = () => {
    const currentGameState = gameStateRef.current;

    // Prevent skip if game is already complete
    if (currentGameState.isGameComplete || currentGameState.gameStatus !== 'playing') {
      logGameContext('warn', 'Attempted to skip on completed game', {
        gameStatus: currentGameState.gameStatus,
        isGameComplete: currentGameState.isGameComplete
      });
      updateGameState({
        error: 'Cannot skip - game is already complete'
      });
      return;
    }

    if (!currentGameState.solution) {
      logGameContext('warn', 'Attempted to skip without a loaded stock');
      return;
    }

    const solutionSymbol = currentGameState.solution.Symbol;
    const todayDate = getCurrentGameTimestamp();

    // Track skip in game_tracking (fire-and-forget, non-blocking)
    // Skip in devMode and replay mode to prevent test/replay data from being saved
    const isReplaySkipTrack = currentGameState.gameDate && currentGameState.gameDate !== getCurrentGameTimestamp();
    if (!devMode && !isReplaySkipTrack) {
      (async () => {
        const { error } = await supabase
          .from('game_tracking')
          .insert({
            user_id: user?.id || null,
            session_id: getOrCreateSessionId(),
            device_id: getOrCreateDeviceId(),
            category: currentCategory,
            game_date: todayDate,
            stock_symbol: solutionSymbol,
            guess_text: 'SKIPPED',
            is_correct: false,
            attempt_number: currentGameState.guesses.length + 1,
            is_skip: true, // NEW: Flag for analytics
            guessed_at: new Date().toISOString()
          });

        if (error) {
          console.error('🔴 [GameTracking] Skip insert failed:', {
            category: currentCategory,
            gameDate: todayDate,
            error: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
        }
      })();
    }

    // Create skip guess entry
    const skipGuess: GuessResult = {
      company: 'SKIPPED',
      isCorrect: false,
      isSkipped: true,
      timestamp: new Date()
    };

    // Calculate next state (same logic as incorrect guess)
    const nextGuesses: GuessResult[] = [...currentGameState.guesses, skipGuess];
    const remainingAttempts = Math.max(currentGameState.attempts - 1, 0);
    const nextVisibleHints = Math.min(currentGameState.visibleHints + 1, MAX_HINTS);

    const nextState: Partial<GameState> = {
      guesses: nextGuesses,
      attempts: remainingAttempts,
      currentRow: currentGameState.currentRow + 1,
      visibleHints: nextVisibleHints,
      error: null,
    };

    // Check if game ends (4th skip = loss)
    if (remainingAttempts <= 0) {
      const timerResult = stopTimer();
      nextState.gameStatus = 'lost';
      nextState.isGameComplete = true;
      nextState.streak = 0;
      nextState.timerResult = timerResult;

      // Update overall streak on skip-to-loss — replays count too
      recordOverallStreak(currentGameState.gameDate || getCurrentGameTimestamp());
      rescheduleAfterPlay(0);
    }

    updateGameState(nextState);

    logGameContext('info', 'Player skipped attempt', {
      category: currentCategory,
      symbol: solutionSymbol,
      remainingAttempts,
      visibleHints: nextVisibleHints
    });

    // Submit result if game is now complete
    // Skip in devMode to prevent test data from being saved
    // Replays are submitted normally — backend detects via gameDate and skips coins/leaderboard
    const skipGameTimestamp = currentGameState.gameDate || getCurrentGameTimestamp();
    if (!devMode && nextState.isGameComplete) {
      const timerResult = nextState.timerResult;
      const gameTimestamp = skipGameTimestamp;
      const submissionKey = `${currentCategory}_${solutionSymbol}_${gameTimestamp}`;

      // Check if we've already submitted for this game
      if (hasSubmittedResultRef.current === submissionKey) {
        logGameContext('warn', 'Result already submitted for this game, skipping duplicate submission', {
          category: currentCategory,
          symbol: solutionSymbol
        });
        return;
      }

      const gameResult = {
        attempts: nextGuesses.length,
        isWin: false,
        solution: solutionSymbol,
        guesses: nextGuesses.map(g => g.company),
        completedAt: new Date().toISOString(),
        category: currentCategory,
        timeTakenMs: timerResult?.elapsedMs || 0,
        hintsUsed: nextVisibleHints,
        skipCount: nextGuesses.filter(g => g.isSkipped).length,
        gameDate: currentGameState.gameDate || undefined,
      };

      // Mark as submitted BEFORE making the API call
      hasSubmittedResultRef.current = submissionKey;

      logGameContext('info', 'Game ended with skips, submitting result', {
        category: currentCategory,
        symbol: solutionSymbol,
        attempts: nextGuesses.length,
        skipCount: gameResult.skipCount
      });

      // Fire and forget - don't block UI on backend submission
      submitGameResult(gameResult).catch((error) => {
        gameContextLogger.error('Failed to submit skip result to backend', { error, gameResult });
      });
    }
  };

  const handleSkip = useCallback(() => {
    handleSkipRef.current?.();
  }, []);

  const initializeGame = useCallback(async () => {
    // This will be implemented in the useGame hook
    logGameContext('debug', 'initializeGame invoked');
  }, []);

  const validateGuessRef = useRef<(guess: string, validOptions?: string[]) => { isValid: boolean; error?: string }>(() => ({ isValid: false }));
  
  validateGuessRef.current = (guess: string, validOptions?: string[]) => {
    const currentGameState = gameStateRef.current;

    if (currentGameState.isGameComplete || currentGameState.gameStatus !== 'playing') {
      return { isValid: false, error: 'This game is already complete. Check back tomorrow for a new puzzle!' };
    }

    const trimmedGuess = guess.trim();
    if (!trimmedGuess) {
      return { isValid: false, error: 'Please enter a guess' };
    }

    if (!currentGameState.solution) {
      return { isValid: false, error: 'Daily stock is still loading. Please try again in a moment.' };
    }

    // Validate against available options if provided
    if (validOptions && validOptions.length > 0) {
      const isValidOption = validOptions.some(option => {
        // Match against the normalized option value
        return option.trim().toLowerCase() === trimmedGuess.toLowerCase();
      });

      if (!isValidOption) {
        return { isValid: false, error: 'Please select a valid stock from the dropdown list.' };
      }
    }

    const normalizedGuess = sanitizeGuess(trimmedGuess);
    const hasDuplicate = currentGameState.guesses.some(previous => sanitizeGuess(previous.company) === normalizedGuess);
    if (hasDuplicate) {
      return { isValid: false, error: "You've already guessed this stock!" };
    }

    return { isValid: true };
  };

  const validateGuess = useCallback((guess: string, validOptions?: string[]) => {
    return validateGuessRef.current?.(guess, validOptions) ?? { isValid: false, error: 'Validation not available' };
  }, []);

  const getGameStatisticsRef = useRef<() => GameStatistics>(() => ({
    gamesPlayed: 0,
    winPercentage: 0,
    currentStreak: 0,
    maxStreak: 0,
    guessDistribution: [0, 0, 0, 0]
  }));
  
  getGameStatisticsRef.current = () => {
    const currentGameState = gameStateRef.current;
    // Basic stats - can be enhanced with category-specific data
    return {
      gamesPlayed: currentGameState.guesses.length > 0 ? 1 : 0,
      winPercentage: currentGameState.gameStatus === 'won' ? 100 : 0,
      currentStreak: currentGameState.streak,
      maxStreak: currentGameState.streak,
      guessDistribution: [0, 0, 0, 0]
    };
  };

  const getGameStatistics = useCallback(() => {
    return getGameStatisticsRef.current?.() ?? {
      gamesPlayed: 0,
      winPercentage: 0,
      currentStreak: 0,
      maxStreak: 0,
      guessDistribution: [0, 0, 0, 0]
    };
  }, []);

  const shareGameRef = useRef<() => string>(() => '');
  
  shareGameRef.current = () => {
    const currentGameState = gameStateRef.current;
    // Build emoji share line (Wordle-style pattern)
    let emojiLine: string;

    // Format category name for display
    const categoryDisplayName = currentCategory === 'popular'
      ? 'Household Name'
      : currentCategory.charAt(0).toUpperCase() + currentCategory.slice(1);

    // Format the game date for share text
    const gameDate = currentGameState.gameDate || getCurrentGameTimestamp();
    const formatted = new Date(gameDate + 'T00:00:00Z');
    const dateSuffix = ` (${formatted.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })})`;

    if (currentGameState.gameStatus === 'won') {
      emojiLine = buildShareLine({ winAt: currentGameState.guesses.length, category: currentCategory });
      let timeSuffix = '';
      if (currentGameState.timerResult?.wasStarted && currentGameState.timerResult.elapsedMs > 0) {
        const totalSeconds = Math.round(currentGameState.timerResult.elapsedMs / 1000);
        const timeStr = `${totalSeconds} seconds`;
        timeSuffix = ` in just ${timeStr}`;
      }
      return `Wallstreetle.com ${categoryDisplayName} Category${dateSuffix} in ${currentGameState.guesses.length}/4 attempts${timeSuffix}.\n\n${emojiLine}`;
    } else {
      emojiLine = buildShareLine({ fail: true, category: currentCategory });
      return `Wallstreetle.com ${categoryDisplayName} Category${dateSuffix} failed.\n\n${emojiLine}`;
    }
  };

  const shareGame = useCallback(() => {
    return shareGameRef.current?.() ?? '';
  }, []);

  // Auto-save when game state changes - use ref to avoid dependency on saveCategoryGameState
  const saveCategoryGameStateRef = useRef(saveCategoryGameState);
  saveCategoryGameStateRef.current = saveCategoryGameState;

  // Intentionally using granular gameState properties to avoid unnecessary localStorage writes.
  // Using the full gameState object would trigger saves on every state change, including
  // transient UI states. saveCategoryGameState is accessed via ref to maintain stability.
  // Skip saving in replay mode (gameDate !== today) to prevent overwriting today's game.
  useEffect(() => {
    const isReplay = gameState.gameDate && gameState.gameDate !== getCurrentGameTimestamp();
    if (gameState.solution?.Symbol && !isReplay) {
      saveCategoryGameStateRef.current(currentCategory, gameState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gameState.solution?.Symbol,
    gameState.guesses.length, // Use length instead of array reference
    gameState.visibleHints,
    gameState.attempts,
    gameState.gameStatus,
    gameState.streak,
    gameState.isGameComplete,
    gameState.timerResult,
    currentCategory
    // Removed saveCategoryGameState dependency - using ref instead
  ]);

  // Load initial category state - use existing ref to avoid callback dependency
  // Skip if switchCategory already handled this category's state load
  useEffect(() => {
    if (lastSwitchedCategoryRef.current === currentCategory) {
      lastSwitchedCategoryRef.current = null;
      return;
    }
    loadGameStateRef.current?.();
  }, [currentCategory]); // Only depend on category change

  // PERFORMANCE OPTIMIZATION: Split context into stable actions and changing state
  // This prevents components that only need actions from re-rendering when state changes
  // Callbacks using refs are now stable and don't need to be listed as dependencies
  const stableActions = useMemo(() => ({
    switchCategory,
    getCategoryGameState,
    resetCategoryGame,
    submitGuess,
    handleGiveUp,
    handleSkip,
    resetGame,
    loadGameState,
    saveGameState,
    updateGameState,
    updateStockData,
    setMarketCapLookup,
    initializeGame,
    validateGuess,
    getGameStatistics,
    shareGame,
    // Dev-only feature (conditionally included)
    ...(process.env.NODE_ENV === 'development' && { devResetCategory })
  }), [
    // Only include callbacks that have actual changing dependencies
    switchCategory,
    getCategoryGameState,
    resetCategoryGame,
    resetGame,
    updateStockData,
    devResetCategory,
    // All other callbacks use refs and are stable
    submitGuess,
    handleGiveUp,
    handleSkip,
    loadGameState,
    saveGameState,
    updateGameState,
    setMarketCapLookup,
    initializeGame,
    validateGuess,
    getGameStatistics,
    shareGame
  ]);

  // PERFORMANCE OPTIMIZATION: Separate changing state to minimize re-render scope
  const changingState = useMemo(() => ({
    currentCategory,
    gameState,
    devMode
  }), [currentCategory, gameState, devMode]);

  // PERFORMANCE OPTIMIZATION: Memoize the complete context value to maintain reference equality
  // This prevents unnecessary re-renders of components consuming the full context
  const contextValue: CategoryGameContextType = useMemo(() => ({
    ...changingState,
    ...stableActions
  }), [changingState, stableActions]);

  return (
    <CategoryGameActionsContext.Provider value={stableActions}>
      <CategoryGameStateContext.Provider value={changingState}>
        <CategoryGameContext.Provider value={contextValue}>
          {children}
        </CategoryGameContext.Provider>
      </CategoryGameStateContext.Provider>
    </CategoryGameActionsContext.Provider>
  );
};

// PERFORMANCE OPTIMIZED HOOKS: Use these for better re-render performance

/**
 * Hook for accessing only game actions (functions).
 * Use this when you only need to call game functions and don't need state updates.
 * This prevents re-renders when game state changes.
 */
export const useCategoryGameActions = (): CategoryGameActions => {
  const context = useContext(CategoryGameActionsContext);
  if (context === undefined) {
    throw new Error('useCategoryGameActions must be used within a CategoryGameProvider');
  }
  return context;
};

/**
 * Hook for accessing only game state (data).
 * Use this when you only need to read game state and don't need action functions.
 * This provides the most up-to-date state while keeping action references stable.
 */
export const useCategoryGameState = (): CategoryGameState => {
  const context = useContext(CategoryGameStateContext);
  if (context === undefined) {
    throw new Error('useCategoryGameState must be used within a CategoryGameProvider');
  }
  return context;
};

// Hook to use the category-aware game context (legacy - combines both contexts)
export const useCategoryGame = (): CategoryGameContextType => {
  const context = useContext(CategoryGameContext);
  if (context === undefined) {
    throw new Error('useCategoryGame must be used within a CategoryGameProvider');
  }
  return context;
};
